import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@klaro7/ui';

import { useCookieConsent } from '../hooks/useCookieConsent';

/** Link informativi, uguali nelle due varianti di testo (mobile e desktop). */
function CookieLinks() {
  return (
    <>
      <Link
        to="/cookie-policy"
        className="font-medium text-brand-teal underline underline-offset-2 hover:text-brand-teal/80"
      >
        Cookie Policy
      </Link>{' '}
      ·{' '}
      <Link
        to="/privacy"
        className="font-medium text-brand-teal underline underline-offset-2 hover:text-brand-teal/80"
      >
        Privacy Policy
      </Link>
    </>
  );
}

/**
 * Primo layer — banner non bloccante pinnato in basso.
 * Pari dignità grafica (Garante): "Accetta tutti" e "Rifiuta tutti" sono
 * entrambi pulsanti pieni, stessa geometria. "Personalizza" è la terza opzione.
 */
export function CookieBanner() {
  const { acceptAll, rejectAll, openPreferences } = useCookieConsent();
  const ref = useRef<HTMLDivElement>(null);

  /**
   * Il banner è `fixed`: su un telefono occupa la fascia bassa dello schermo e
   * ci finiscono sotto le CTA delle pagine a tutto schermo (verifica OTP,
   * login, registrazione). Lì il tap non arriva al bottone — muore sul banner —
   * e sembra che il bottone non funzioni. Pubblicando l'altezza come variabile
   * CSS le pagine possono riservarsi lo spazio finché il consenso manca.
   */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty('--cookie-banner-h', `${Math.ceil(el.getBoundingClientRect().height)}px`);

    publish();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    observer?.observe(el);
    window.addEventListener('resize', publish);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', publish);
      root.style.setProperty('--cookie-banner-h', '0px');
    };
  }, []);

  return (
    <div
      ref={ref}
      role="region"
      aria-label="Gestione consenso cookie"
      // pointer-events-auto: un modale Radix aperto (popup di benvenuto, login)
      // mette `pointer-events: none` sul body e il banner, che sta fuori dal
      // modale, diventava intoccabile: senza scelta non si poteva proseguire.
      className="pointer-events-auto fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-4 motion-safe:animate-slide-up"
      style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
    >
      <div className="mx-auto max-w-4xl rounded-2xl border border-neutral-200 bg-white p-4 shadow-xl shadow-brand-navy/10 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="text-sm leading-relaxed text-neutral-700">
            <h2 className="mb-1 text-base font-semibold text-neutral-900">
              Rispettiamo la tua privacy
            </h2>
            {/* Testo corto su telefono: la versione lunga faceva un banner alto
                metà schermo, che copriva le CTA delle pagine sotto. Il dettaglio
                resta in Cookie Policy e in "Personalizza". */}
            <p className="sm:hidden">
              Cookie tecnici e, con il tuo consenso, analitici e di profilazione.{' '}
              <CookieLinks />
            </p>
            <p className="hidden sm:block">
              Usiamo cookie tecnici necessari al funzionamento del sito e, con il tuo consenso,
              cookie analitici anonimizzati e di profilazione per migliorare la tua esperienza.
              Puoi accettare, rifiutare o scegliere quali attivare. <CookieLinks />
            </p>
          </div>

          {/* Su telefono i tre bottoni stanno in riga: impilati il banner mangiava
              quasi metà schermo e copriva le CTA delle pagine sotto. */}
          <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-shrink-0 sm:flex-row">
            <Button
              onClick={rejectAll}
              className="w-full whitespace-normal bg-brand-teal px-2 font-semibold leading-tight text-white hover:bg-brand-teal/90 sm:w-auto sm:px-4"
            >
              Rifiuta tutti
            </Button>
            <Button
              onClick={openPreferences}
              variant="outline"
              className="w-full whitespace-normal border-2 border-brand-navy px-2 font-semibold leading-tight text-brand-navy hover:bg-brand-navy/5 sm:w-auto sm:px-4"
            >
              Personalizza
            </Button>
            <Button
              onClick={acceptAll}
              className="w-full whitespace-normal bg-brand-orange px-2 font-semibold leading-tight text-white hover:bg-brand-orange/90 sm:w-auto sm:px-4"
            >
              Accetta tutti
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
